'use client'

import { useQueries } from '@tanstack/react-query'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import {
  aggregateCompareResults,
  deserializeChartSymbolKeys,
  filterCompareSymbols,
  serializeChartSymbolKeys,
} from '@/features/chart/lib/chart-compare'
import { LinkingPageContext } from '@/features/dashboard/lib/linking-page-context'
import { useLinkingColor } from '@/hooks/use-linking-group-state'
import { toExchangeEnum, tradeVenueToExchangeKey } from '@/lib/proto/util'
import { toast } from '@/lib/toast'
import { type ChartSymbolIdentity, chartSymbolKey } from '@/lib/tradingview/chart-symbol-identity'
import { accountExchangeKeys, fetchAccountExchanges } from '@/service/account-exchanges.hook'
import { useUserAccounts } from '@/service/account.hook'
import { instrumentListQueryOptions } from '@/service/instrument.hook'
import {
  chartCompareCommandsAtomFamily,
  type ChartCompareMode,
  type ChartCompareResult,
  chartCompareResultsAtomFamily,
  chartCompareTargetsAtomFamily,
} from '@/state/chart-compare'
import { AccountStatus } from '@/types/generated/common/account'
import { Exchange, TradeVenue } from '@/types/generated/common/types'

let nextCompareRequestId = Date.now()
const COMPARE_RESULT_TIMEOUT_MS = 8_000

type PendingCompareRequest = {
  requestId: number
  targetChartIds: string[]
  validationSkipped: number
}

function fallbackExchange(tradeVenue: TradeVenue): Exchange {
  if (tradeVenue === TradeVenue.TRADE_VENUE_IBKR) return Exchange.EXCHANGE_UNSPECIFIED
  return toExchangeEnum(tradeVenueToExchangeKey(tradeVenue))
}

export function useChartCompare(cardId: string, options?: { registerTarget?: boolean }) {
  const t = useTranslations('trade.chartToolbar')
  const pageId = useContext(LinkingPageContext)
  const linkingColor = useLinkingColor(cardId)
  const commands = useAtomValue(chartCompareCommandsAtomFamily(pageId))
  const targets = useAtomValue(chartCompareTargetsAtomFamily(pageId))
  const results = useAtomValue(chartCompareResultsAtomFamily(pageId))
  const setCommands = useSetAtom(chartCompareCommandsAtomFamily(pageId))
  const setTargets = useSetAtom(chartCompareTargetsAtomFamily(pageId))
  const setResults = useSetAtom(chartCompareResultsAtomFamily(pageId))
  const accountsQuery = useUserAccounts({ showDisabled: true })
  const pendingRequestRef = useRef<PendingCompareRequest | null>(null)
  const resultTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const targetKey = linkingColor === 'unlinked' ? `card:${cardId}` : `group:${linkingColor}`

  useEffect(() => {
    if (!options?.registerTarget) return
    setTargets((current) => {
      const group = current[targetKey] ?? []
      if (group.includes(cardId)) return current
      return { ...current, [targetKey]: [...group, cardId].sort() }
    })
    return () => {
      setTargets((current) => {
        const currentGroup = current[targetKey] ?? []
        const group = currentGroup.filter((targetId) => targetId !== cardId)
        if (group.length === currentGroup.length) return current
        const next = { ...current }
        if (group.length > 0) next[targetKey] = group
        else delete next[targetKey]
        return next
      })
    }
  }, [cardId, options?.registerTarget, setTargets, targetKey])

  useEffect(
    () => () => {
      if (resultTimeoutRef.current) clearTimeout(resultTimeoutRef.current)
    },
    []
  )

  const activeTradeVenues = useMemo(
    () => [
      ...new Set(
        (accountsQuery.data?.accounts ?? [])
          .filter((account) => account.status === AccountStatus.ACCOUNT_STATUS_ACTIVE)
          .map((account) => account.tradeVenue)
          .filter((venue) => venue !== TradeVenue.TRADE_VENUE_UNSPECIFIED)
      ),
    ],
    [accountsQuery.data?.accounts]
  )

  const exchangeResults = useQueries({
    queries: activeTradeVenues.map((tradeVenue) => ({
      queryKey: accountExchangeKeys.byVenue(tradeVenue),
      queryFn: () => fetchAccountExchanges(tradeVenue),
      staleTime: 60 * 60 * 1000,
    })),
  })

  const allowedExchangeKey = exchangeResults
    .flatMap((result, index) => {
      if (!result.isSuccess) return []
      if ((result.data?.length ?? 0) > 0) return result.data ?? []
      const fallback = fallbackExchange(activeTradeVenues[index])
      return fallback === Exchange.EXCHANGE_UNSPECIFIED ? [] : [fallback]
    })
    .filter((exchange, index, exchanges) => exchanges.indexOf(exchange) === index)
    .sort((left, right) => left - right)
    .join(',')

  const allowedExchanges = useMemo(
    () =>
      new Set<Exchange>(
        allowedExchangeKey
          .split(',')
          .filter(Boolean)
          .map((exchange) => Number(exchange) as Exchange)
      ),
    [allowedExchangeKey]
  )
  const allowedExchangeList = useMemo(
    () => [...allowedExchanges].sort((left, right) => left - right),
    [allowedExchanges]
  )
  const instrumentResults = useQueries({
    queries: allowedExchangeList.map((exchange) => instrumentListQueryOptions(exchange)),
  })
  // serializeChartSymbolKeys already dedupes (via Set) — no need to dedupe the
  // input first. Memoized: instrumentResults is a long list per active exchange
  // (option-heavy exchanges can have thousands), and this hook is mounted by
  // every watchlist/position/chart widget on the page.
  const activeInstrumentKey = useMemo(
    () =>
      serializeChartSymbolKeys(
        instrumentResults.flatMap((result) =>
          result.isSuccess
            ? (result.data?.instruments ?? [])
                .filter((instrument) => instrument.isActive)
                .map((instrument) =>
                  chartSymbolKey({ exchange: instrument.exchange, symbol: instrument.symbol })
                )
            : []
        )
      ),
    [instrumentResults]
  )
  const activeInstrumentKeys = useMemo(
    () => deserializeChartSymbolKeys(activeInstrumentKey),
    [activeInstrumentKey]
  )

  const isResolved =
    accountsQuery.isSuccess &&
    exchangeResults.every((result) => result.isSuccess) &&
    instrumentResults.every((result) => result.isSuccess)

  useEffect(() => {
    const pending = pendingRequestRef.current
    if (!pending) return
    const aggregate = aggregateCompareResults(
      pending.targetChartIds,
      results[pending.requestId],
      pending.validationSkipped
    )
    if (!aggregate.complete) return

    pendingRequestRef.current = null
    if (resultTimeoutRef.current) clearTimeout(resultTimeoutRef.current)
    resultTimeoutRef.current = null
    setResults((current) => {
      const next = { ...current }
      delete next[pending.requestId]
      return next
    })

    if (aggregate.failed > 0) {
      toast.error(
        `Failed to create ${aggregate.failed} comparison series${aggregate.failed === 1 ? '' : 's'}.`
      )
    }
  }, [results, setResults])

  const acknowledge = useCallback(
    (requestId: number, chartId: string, result: ChartCompareResult) => {
      setResults((current) => ({
        ...current,
        [requestId]: { ...current[requestId], [chartId]: result },
      }))
    },
    [setResults]
  )

  const addAsCompare = useCallback(
    (symbols: ChartSymbolIdentity[], mode: ChartCompareMode = 'percent') => {
      if (!isResolved) {
        return
      }

      const targetChartIds = targets[targetKey] ?? []
      if (targetChartIds.length === 0) {
        return
      }

      const { valid, skipped } = filterCompareSymbols(
        symbols,
        allowedExchanges,
        activeInstrumentKeys
      )
      if (valid.length === 0) {
        toast.error(t('compareNoInstruments'))
        return
      }

      const requestId = nextCompareRequestId++
      const targetSnapshot = [...targetChartIds]
      pendingRequestRef.current = {
        requestId,
        targetChartIds: targetSnapshot,
        validationSkipped: skipped,
      }
      setResults((current) => ({ ...current, [requestId]: {} }))
      setCommands((current) => ({
        ...current,
        [targetKey]: { requestId, symbols: valid, targetChartIds: targetSnapshot, mode },
      }))
      if (resultTimeoutRef.current) clearTimeout(resultTimeoutRef.current)
      resultTimeoutRef.current = setTimeout(() => {
        if (pendingRequestRef.current?.requestId !== requestId) return
        pendingRequestRef.current = null
        toast.error(t('compareTimeout'))
        setResults((current) => {
          const next = { ...current }
          delete next[requestId]
          return next
        })
      }, COMPARE_RESULT_TIMEOUT_MS)
    },
    [
      activeInstrumentKeys,
      allowedExchanges,
      isResolved,
      setCommands,
      setResults,
      t,
      targetKey,
      targets,
    ]
  )

  const targetChartIds = targets[targetKey] ?? []
  return {
    acknowledge,
    addAsCompare,
    allowedExchanges,
    command: commands[targetKey],
    isResolved,
    linkingColor,
    canCompare: isResolved && targetChartIds.length > 0,
  }
}
